import { forwardRef } from "react";
import {
  WidgetSettings,
  WidgetInstance,
} from "../../../models/Dashboard";

interface WidgetContainerProps<
  TData = any,
  TSettings extends WidgetSettings = WidgetSettings
> {
  widget: WidgetInstance<TData, TSettings>;
  onRefresh?: () => void;
  onOpenSettings?: () => void;
  onRemove: () => void;
}

export const WidgetContainer = forwardRef<HTMLDivElement, WidgetContainerProps>(
  ({ widget, onRefresh, onOpenSettings }, ref) => {
  const { definition, settings, dataState } = widget;

  const renderContent = () => {
    switch (dataState.status) {
      case "loading":
        return (
          <div className="widget-state-container">
            <div className="spinner-border text-secondary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        );

      case "error":
        return (
          <div className="widget-state-container widget-error">
            <div className="widget-error-icon">
              <i className="bi bi-exclamation-triangle"></i>
            </div>
            <div className="widget-error-message">
              <strong>Error loading widget</strong>
              <p className="text-muted small">{dataState.error?.message}</p>
            </div>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={onRefresh}
            >
              Try Again
            </button>
          </div>
        );

      case "empty":
        return (
          <div className="widget-state-container widget-empty">
            <div className="widget-empty-icon">
              <i className={definition.icon || "bi bi-inbox"}></i>
            </div>
            <div className="widget-empty-message">
              <strong>{definition.title}</strong>
              <p className="text-muted mb-0">No data for selected filters/date.</p>
            </div>
          </div>
        );

      case "success":
        return definition.render({
          data: dataState.data,
          dataState,
          settings,
          isLoading: false,
          onRefresh,
        });

      default:
        return null;
    }
  };

  return (
    <div ref={ref} className="dashboard-widget">
      <div className="widget-card">
        <div className="widget-header">
          <span className="widget-header-icon" aria-hidden="true">
            <i className={definition.icon || "bi bi-grid-3x3-gap"}></i>
          </span>

          <span className="widget-title">{definition.title}</span>

          <div className="widget-header-right">
            <button
              className="btn btn-sm btn-outline-secondary widget-icon-btn"
              title={`Open ${definition.title} settings`}
              aria-label={`Open ${definition.title} settings`}
              onClick={() => onOpenSettings?.()}
            >
              <i className="bi bi-sliders" aria-hidden="true"></i>
            </button>
            {definition.supportsRefresh !== false && (
              <button
                className="btn btn-sm btn-outline-secondary widget-icon-btn"
                title={`Refresh ${definition.title}`}
                aria-label={`Refresh ${definition.title}`}
                onClick={onRefresh}
                disabled={dataState.status === "loading"}
              >
                <i className="bi bi-arrow-clockwise" aria-hidden="true"></i>
              </button>
            )}
            <button
              className="btn btn-sm btn-outline-secondary widget-icon-btn widget-drag"
              title={`Drag to move ${definition.title}`}
              aria-label={`Drag to move ${definition.title}`}
            >
              <i className="bi bi-arrows-move" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div className="widget-body">{renderContent()}</div>
      </div>
    </div>
  );
});

WidgetContainer.displayName = "WidgetContainer";

export const DefaultSettingsPanel = ({
  settings,
  defaultSettings,
  onSettingsChange,
  isSaving,
}: {
  settings: WidgetSettings;
  defaultSettings?: WidgetSettings;
  onSettingsChange: (settings: WidgetSettings) => void;
  isSaving?: boolean;
}) => {
  const handleChange = (key: string, value: any) => {
    onSettingsChange({
      ...settings,
      [key]: value,
    });
  };

  return (
    <section className="settings-section">
      <h6>Settings</h6>
      {Object.entries(settings).map(([key, value]) => (
        <div key={key} className="mb-3">
          <label className="form-label" htmlFor={key}>
            {formatLabel(key)}
          </label>

          {typeof value === "boolean" ? (
            <div className="form-check">
              <input
                id={key}
                className="form-check-input"
                type="checkbox"
                checked={value}
                onChange={(e) => handleChange(key, e.target.checked)}
                disabled={isSaving}
              />
              <label className="form-check-label" htmlFor={key}>
                Enabled
              </label>
            </div>
          ) : key === "legendPosition" ? (
            <select
              id={key}
              className="form-select form-select-sm"
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              disabled={isSaving}
            >
              <option value="top">Top</option>
              <option value="bottom">Bottom</option>
            </select>
          ) : key === "topN" ? (
            <select
              id={key}
              className="form-select form-select-sm"
              value={value}
              onChange={(e) => handleChange(key, parseInt(e.target.value))}
              disabled={isSaving}
            >
              <option value="5">Top 5</option>
              <option value="10">Top 10</option>
              <option value="15">Top 15</option>
              <option value="20">Top 20</option>
                          </select>
          ) : key === "dateRange" ? (
            <select
                id={key}
                className="form-select form-select-sm"
                value={value}
                onChange={(e) => handleChange(key, parseInt(e.target.value))}
                disabled={isSaving}
            >
                <option value="1">Last Day</option>
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
            </select>
          ) : key === "sortBy" ? (
            <select
              id={key}
              className="form-select form-select-sm"
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              disabled={isSaving}
            >
              <option value="count">By Count</option>
              <option value="amount">By Amount</option>
            </select>
          ) : key === "sortOrder" ? (
            <select
              id={key}
              className="form-select form-select-sm"
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              disabled={isSaving}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          ) : key === "showMetric" ? (
            <select
              id={key}
              className="form-select form-select-sm"
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              disabled={isSaving}
            >
              <option value="volume">Volume</option>
              <option value="count">Count</option>
              <option value="both">Both</option>
            </select>
          ) : typeof value === "number" ? (
            <input
              id={key}
              className="form-control form-control-sm"
              type="number"
              value={value}
              onChange={(e) => handleChange(key, parseInt(e.target.value))}
              disabled={isSaving}
            />
          ) : typeof value === "string" && value.match(/^#[0-9A-F]{6}$/i) ? (
            <input
              id={key}
              className="form-control form-control-color"
              type="color"
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              disabled={isSaving}
            />
          ) : (
            <input
              id={key}
              className="form-control form-control-sm"
              type="text"
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              disabled={isSaving}
            />
          )}
        </div>
      ))}

      {defaultSettings && (
        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onSettingsChange({ ...defaultSettings })}
            disabled={isSaving}
          >
            Reset to defaults
          </button>
        </div>
      )}
    </section>
  );
};

const formatLabel = (str: string): string => {
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase())
    .trim();
};
